import {ChangeDetectionStrategy, Component, DestroyRef, HostListener, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {EMPTY, Observable, of} from 'rxjs';
import {catchError, finalize, map, switchMap, take, tap} from 'rxjs/operators';
import {IntroductionComponent} from '../shared/introduction/introduction.component';
import {AlertComponent} from '../shared/alert/alert.component';
import {LoaderComponent} from '../shared/loader/loader.component';
import {UnemploymentEndDateComponent} from '../shared/unemployment-end-date/unemployment-end-date.component';
import {MemberStateService} from '../../services/member-state.service';
import {FormatService} from '../../services/format.service';
import {FileService} from '../../services/file.service';
import {PaymentService} from '../../services/payment.service';
import {MemberService} from '../../services/member.service';
import {LettersService} from '../../services/letters.service';
import {UserContextService} from '../../services/user-context.service';
import {AnalyticsService} from '../../services/analytics.service';
import {Member} from '../../model/member';
import {LocalizedText} from '../../model/localized-text';
import {ApplicationResponse} from '../../model/application-response';
import {isEuroMembershipFee} from '../../view/membership-fee.view';
import {LetterResponse} from '../../model/letter-response';
import {ViewState, toViewState} from '../../model/view-state';
import {
  MembershipFeeVM,
  PaymentMomentVM,
  UnemploymentPaymentRequestVM,
  ApplicationResponseVM,
} from '../../view/recent-infos.view';
import {UnemploymentApplicationStatus} from '../../model/unemployment-application-status';
import {IncompleteRequestInfoComponent} from '../shared/incomplete-request-info/incomplete-request-info.component';

interface MembershipFeeData {
  fee: MembershipFeeVM | null;
  // True when pre-euro (Belgian franc) fees were left out
  hasHiddenPreEuroFees: boolean;
}

interface RequestData {
  paymentRequest: UnemploymentPaymentRequestVM | null;
  application: ApplicationResponseVM | null;
}

@Component({
    selector: 'myabvv-recent-infos',
    templateUrl: './recent-infos.component.html',
    styleUrls: ['./recent-infos.component.css'],
    standalone: true,
    imports: [
      CommonModule, RouterModule, TranslateModule, IntroductionComponent, AlertComponent,
      LoaderComponent, UnemploymentEndDateComponent, IncompleteRequestInfoComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecentInfosComponent implements OnInit {
  private memberStateService = inject(MemberStateService);
  private formatService = inject(FormatService);
  private fileService = inject(FileService);
  private paymentService = inject(PaymentService);
  private memberService = inject(MemberService);
  private lettersService = inject(LettersService);
  private userContext = inject(UserContextService);
  private analyticsService = inject(AnalyticsService);

  paymentState$: Observable<ViewState<PaymentMomentVM | null>>;
  requestState$: Observable<ViewState<RequestData>>;
  membershipFeeState$: Observable<ViewState<MembershipFeeData>>;

  isDownloading = signal(false);
  hasDownloadError = signal(false);

  // Popover
  isPopoverVisible = false;
  popoverPosition: { top: number; left: number } = { top: 0, left: 0 };

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.paymentState$ = this.buildPaymentSource$().pipe(toViewState());
    this.requestState$ = this.buildRequestSource$().pipe(toViewState());
    this.membershipFeeState$ = this.buildMembershipFeeSource$().pipe(toViewState());
  }

  getTranslatedData(x: LocalizedText[] | null | undefined): string {
    return this.formatService.getTranslatedData(x);
  }

  getRequestType(request: UnemploymentPaymentRequestVM): string {
    return this.formatService.getRequestType(request.raw);
  }

  // /membershipfees sends a plain number (e.g. "640.00"): format it like the other amounts
  formatContributionAmount(amount: string | undefined): string {
    return amount ? this.formatService.formatNumber(amount) : '';
  }

  getCurrency(code: string): string {
    return this.formatService.formatCurrency(code);
  }

  getUnemploymentGrossAmount(payment: PaymentMomentVM): string {
    return this.formatService.formatNumber(payment.sumUnemploymentGrossAmount());
  }

  getUnemploymentDays(payment: PaymentMomentVM): string {
    return this.formatService.formatNumber(payment.sumUnemploymentDays(), 1);
  }

  getAdditionalGrossAmount(payment: PaymentMomentVM): string {
    return this.formatService.formatNumber(payment.sumAdditionalCompensationGrossAmount());
  }

  getAdditionalDays(payment: PaymentMomentVM): string {
    return this.formatService.formatNumber(payment.sumAdditionalCompensationDays(), 1);
  }

  getStatusBadgeClass(request: UnemploymentPaymentRequestVM): string {
    const state = request.raw.requestState;
    switch (state) {
      case UnemploymentApplicationStatus.COMPLETED:
        return 'c-status-badge--success';
      case UnemploymentApplicationStatus.IMPOSSIBLE:
        return 'c-status-badge--error';
      case UnemploymentApplicationStatus.CLOSED:
        return 'c-status-badge--neutral';
      default:
        return 'c-status-badge--warning';
    }
  }

  getStatusIcon(request: UnemploymentPaymentRequestVM): string {
    const state = request.raw.requestState;
    switch (state) {
      case UnemploymentApplicationStatus.COMPLETED:
        return 'fa-check-circle';
      case UnemploymentApplicationStatus.IMPOSSIBLE:
        return 'fa-times-circle';
      case UnemploymentApplicationStatus.CLOSED:
        return 'fa-minus-circle';
      default:
        return 'fa-clock';
    }
  }

  togglePopover(event: Event): void {
    event.stopPropagation();

    if (this.isPopoverVisible) {
      this.isPopoverVisible = false;
    } else {
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();

      // Position popover below the trigger
      this.popoverPosition = {
        top: rect.bottom + window.scrollY + 8,
        left: Math.min(rect.left + window.scrollX, window.innerWidth - 340)
      };

      this.isPopoverVisible = true;
    }
  }

  closePopover(): void {
    this.isPopoverVisible = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close popover when clicking outside
    if (this.isPopoverVisible) {
      const target = event.target as HTMLElement;
      if (!target.closest('.popover-container') && !target.closest('.recent-infos-popover')) {
        this.closePopover();
      }
    }
  }

  downloadRequest(request: UnemploymentPaymentRequestVM): void {
    this.isDownloading.set(true);
    this.hasDownloadError.set(false);
    const isInternalUser = this.userContext.isInternalUser();

    this.analyticsService.trackEvent('download_payment_request', {
      status: request.raw.requestState
    });

    // Use /me endpoint for external users, /{inss} for internal users
    const download$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          switchMap((m: Member) =>
            this.lettersService.getPaymentRequests(
              m.personalInformation.inss,
              request.raw.creationDate,
              request.raw.docNumber,
              request.raw.docRegion
            )
          )
        )
      : this.lettersService.getMyPaymentRequests(
          request.raw.creationDate,
          request.raw.docNumber,
          request.raw.docRegion
        );

    download$.pipe(
      tap((res: LetterResponse) => {
        this.fileService.downloadFile(res);
      }),
      catchError(() => {
        this.hasDownloadError.set(true);
        this.analyticsService.trackEvent('download_failed', { type: 'payment_request' });
        return EMPTY;
      }),
      finalize(() => {
        this.isDownloading.set(false);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private buildPaymentSource$(): Observable<PaymentMomentVM | null> {
    const isInternalUser = this.userContext.isInternalUser();

    // Use /me endpoint for external users, /{inss} for internal users
    const lastPayment$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          switchMap((m: Member) => this.paymentService.getLastPayment(m.personalInformation.inss))
        )
      : this.paymentService.getMyLastPayment();

    return lastPayment$.pipe(
      map(pm => pm ? new PaymentMomentVM(pm) : null)
    );
  }

  private buildRequestSource$(): Observable<RequestData> {
    const isInternalUser = this.userContext.isInternalUser();
    const statuses = [
      UnemploymentApplicationStatus.INCOMPLETE,
      UnemploymentApplicationStatus.EXTEND_DEADLINE,
      UnemploymentApplicationStatus.EXPECTED_EXTENDED_DEADLINE,
    ];

    const buildApplicationPipe = (reqVM: UnemploymentPaymentRequestVM | null, getApplication$: Observable<ApplicationResponse>) => {
      if (reqVM && reqVM.isIncomplete()) {
        return getApplication$.pipe(
          map(app => {
            if (!app) return { paymentRequest: reqVM, application: null };
            const appVM = new ApplicationResponseVM(app);
            return {
              paymentRequest: reqVM,
              application: appVM.matchesCutoffAndSameDayAs(reqVM) ? appVM : null
            };
          })
        );
      }
      return of({ paymentRequest: reqVM, application: null });
    };

    // Use /me endpoints for external users, /{inss} for internal users
    if (isInternalUser) {
      return this.memberStateService.getMember().pipe(
        take(1),
        switchMap((member: Member) =>
          this.lettersService.getLastPaymentRequest(member.personalInformation.inss).pipe(
            map(req => req ? new UnemploymentPaymentRequestVM(req) : null),
            switchMap((reqVM: UnemploymentPaymentRequestVM | null) =>
              buildApplicationPipe(reqVM, this.lettersService.getLastUnemploymentApplication(
                member.personalInformation.inss, statuses
              ))
            )
          )
        )
      );
    }

    return this.lettersService.getMyLastPaymentRequest().pipe(
      map(req => req ? new UnemploymentPaymentRequestVM(req) : null),
      switchMap((reqVM: UnemploymentPaymentRequestVM | null) =>
        buildApplicationPipe(reqVM, this.lettersService.getMyLastUnemploymentApplication(statuses))
      )
    );
  }

  private buildMembershipFeeSource$(): Observable<MembershipFeeData> {
    const isInternalUser = this.userContext.isInternalUser();

    // Use /me endpoint for external users, /{inss} for internal users
    const membershipFees$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          switchMap((member: Member) => this.memberService.getMemberShipFees(member.personalInformation.inss))
        )
      : this.memberService.getMyMemberShipFees();

    return membershipFees$.pipe(
      map(res => {
        const fees = Array.isArray(res) ? res : [];
        const euroFees = fees.filter(isEuroMembershipFee);
        const hasHiddenPreEuroFees = euroFees.length < fees.length;
        if (!euroFees.length) return { fee: null, hasHiddenPreEuroFees };
        // Most recent period first: the payment date is misleading (catch-ups, several months paid the same day).
        // A multi-month period counts by its last month; same period: most recent payment first.
        const periodEnd = (fee: MembershipFeeVM) =>
          (fee.contributionPeriodToMonthDate ?? fee.contributionPeriodFromMonthDate)?.getTime() ?? 0;
        const paymentTime = (fee: MembershipFeeVM) => fee.dateOfPaymentDate?.getTime() ?? 0;
        const sorted = euroFees
          .map(fee => new MembershipFeeVM(fee))
          .sort((a, b) => periodEnd(b) - periodEnd(a) || paymentTime(b) - paymentTime(a));
        return { fee: sorted[0], hasHiddenPreEuroFees };
      })
    );
  }
}
